import { Post, allPosts } from 'contentlayer/generated'
import type { GetStaticPaths } from 'next'
import { useMDXComponent } from 'next-contentlayer/hooks'
import Image from 'next/image'

import { useMounted } from 'hooks/use-mounted'
import { formatMdxDate } from 'lib/utils/date'

import Box from 'components/atomic/atoms/box'
import Text from 'components/atomic/atoms/text'
import LayoutWebsite from 'components/pages/@layouts/layout-website'
import SharedCta from 'components/pages/@shared/cta'
import sharedComponents from 'components/pages/@shared/mdx'
import ShareButtons from 'components/pages/@shared/share-buttons'

const BlogSlugPage = ({ post }: { post: Post }) => {
  const mounted = useMounted()
  const { title, imageUrl, description, authors, date, metaTitle, cta } = post
  const MDXComponent = useMDXComponent(post?.body?.code)

  return (
    <LayoutWebsite meta={{ title: metaTitle || title, imageUrl, description }}>
      <Box
        flex="column"
        css={{
          alignItems: 'center',
          py: '$9',
          spaceY: 4,
          bgLinearGradient:
            '0deg, rgba(181, 219, 219, 0.15) 0%, rgba(5, 102, 133, 0) 86.48%',
          '.dark &': {
            bgLinearGradient:
              '0deg, rgba(21, 138, 138, 0.15) 0%, rgba(5, 102, 133, 0) 86.48%',
          },
        }}
        textCenter
      >
        <Text
          as="h1"
          color="text1"
          size={{ '@initial': '2xl', '@sm': '4xl' }}
          weight="bold"
          css={{
            container: 'md',
          }}
        >
          {title}
        </Text>
        <Box>
          <Text as="time" color="text2">
            {mounted ? formatMdxDate(date) : '...'}
          </Text>
        </Box>
        <Box
          grid
          css={{
            spaceX: 4,
            gridAutoFlow: 'column',
            gridAutoColumns: '1fr',
          }}
        >
          {authors?.map(({ name, imageUrl, twitter, socialUrl }, index) => {
            const href = socialUrl?.startsWith('https://')
              ? socialUrl
              : twitter
                ? `https://twitter.com/${twitter}`
                : null

            return (
              <Box
                key={index}
                as={href ? 'a' : 'span'}
                flex="column"
                items="center"
                justify="center"
                css={{
                  spaceY: 1,
                  '&:hover': href ? { color: '$text2' } : {},
                }}
                {...(href ? { href, target: '_blank', rel: 'noopener' } : {})}
              >
                <Box
                  as={Image}
                  src={imageUrl}
                  width={40}
                  height={40}
                  alt={name}
                  css={{ borderRadius: '$round' }}
                />
                <Text size="sm">{name}</Text>
              </Box>
            )
          })}
        </Box>
      </Box>
      <Box css={{ pt: '$12' }}>
        <Box css={{ container: 'md' }}>
          <MDXComponent components={sharedComponents} />
        </Box>
        <ShareButtons title={title} />
      </Box>
      <Box css={{ pt: '$12' }}>
        <SharedCta {...cta} />
      </Box>
    </LayoutWebsite>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = allPosts
    .filter(p => !p.hidden)
    .filter(p => p.category !== 'changelog')
    .map(post => post.url)

  return {
    paths,
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params }) {
  const post = allPosts
    .filter(p => !p.hidden)
    .find(post => post.url === `/blog/${params.slug}`)

  if (!post) {
    return {
      notFound: true,
    }
  }

  const { title, imageUrl, description, authors, date, metaTitle, cta } = post

  return {
    props: {
      post: {
        title,
        imageUrl,
        description,
        authors,
        date,
        metaTitle: metaTitle ?? null,
        cta: cta ?? null,
        body: { code: post.body.code },
      },
    },
  }
}

export default BlogSlugPage
